import {
    BankTransactionConversionRate,
    BankTransferInvoicePayment,
    InvoicePaymentUpdateOrCreateDto,
} from 'types/bank-transfers.ts';

export interface RowConversion {
    rate: number;
    date?: string;
}

export function getRowConversion(
    row: BankTransferInvoicePayment,
    transactionCurrencyTypeCode: string,
    fetchedRates: Record<number, BankTransactionConversionRate>
): RowConversion | undefined {
    if (row.invoicePaymentCurrencyTypeCode === transactionCurrencyTypeCode) {
        return { rate: 1 };
    }
    if (isUsableRate(row.conversionRate)) {
        return { rate: Number(row.conversionRate), date: row.conversionDate };
    }
    const fetchedRate = fetchedRates[row.invoiceId];
    return isUsableRate(fetchedRate?.conversionRate)
        ? { rate: Number(fetchedRate.conversionRate), date: fetchedRate.conversionDate }
        : undefined;
}

// The rate is a divisor, so anything non-finite or non-positive leaves the row unresolved.
export function isUsableRate(rate: string | undefined): boolean {
    const parsed = Number(rate);
    return !!rate && Number.isFinite(parsed) && parsed > 0;
}

// Matches the numeric(15,6) precision the rate is persisted with on the invoice payment.
export function formatConversionRate(rate: number): string {
    return String(parseFloat(rate.toFixed(6)));
}

export function isAmountInTransactionCurrency(row: BankTransferInvoicePayment): boolean {
    return row.amountCurrencyTypeCode !== row.invoicePaymentCurrencyTypeCode;
}

/**
 * An unchanged saved payment reports its persisted counterpart instead of a re-conversion, so both
 * figures stay exactly what the backend stored instead of drifting by a rounding cent.
 */
export function toAmountInTransactionCurrency(row: BankTransferInvoicePayment, rate: number): number {
    if (isAmountInTransactionCurrency(row)) {
        return Number(row.amount || 0);
    }
    if (isUnchangedSavedAmount(row.amount, row.savedAmount) && hasValue(row.savedAmountInTransactionCurrency)) {
        return Number(row.savedAmountInTransactionCurrency);
    }
    return roundToCents(Number(row.amount || 0) / rate);
}

export function toAmountInInvoiceCurrency(row: BankTransferInvoicePayment, rate: number): number {
    if (!isAmountInTransactionCurrency(row)) {
        return Number(row.amount || 0);
    }
    if (isUnchangedSavedAmount(row.amount, row.savedAmountInTransactionCurrency) && hasValue(row.savedAmount)) {
        return Number(row.savedAmount);
    }
    return roundToCents(Number(row.amount || 0) * rate);
}

export function switchAmountCurrency(
    row: BankTransferInvoicePayment,
    transactionCurrencyTypeCode: string,
    rate: number
): Pick<BankTransferInvoicePayment, 'amount' | 'amountCurrencyTypeCode'> {
    if (isAmountInTransactionCurrency(row)) {
        return {
            amount: row.amount ? String(toAmountInInvoiceCurrency(row, rate)) : row.amount,
            amountCurrencyTypeCode: row.invoicePaymentCurrencyTypeCode,
        };
    }
    return {
        amount: row.amount ? String(toAmountInTransactionCurrency(row, rate)) : row.amount,
        amountCurrencyTypeCode: transactionCurrencyTypeCode,
    };
}

export function toInvoicePaymentUpdateDto(row: BankTransferInvoicePayment): InvoicePaymentUpdateOrCreateDto {
    const dto: InvoicePaymentUpdateOrCreateDto = {
        invoicePaymentId: row.invoicePaymentId,
        invoiceId: row.invoiceId,
    };
    if (isAmountInTransactionCurrency(row)) {
        dto.amountInTransactionCurrency = row.amount;
    } else {
        dto.amount = row.amount;
    }
    return dto;
}

function isUnchangedSavedAmount(amount: string | undefined, savedAmount: string | undefined): boolean {
    return hasValue(amount) && hasValue(savedAmount) && Number(amount) === Number(savedAmount);
}

function hasValue(amount: string | undefined): amount is string {
    return amount !== undefined && amount !== null && amount !== '';
}

function roundToCents(value: number): number {
    return Math.round(value * 100) / 100;
}
